import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IntroJourney } from '../onboarding/IntroJourney';
import { type JourneyPayload } from '../onboarding/payload';
import { JourneyGeneratingScreen } from '../onboarding/screens/JourneyGeneratingScreen';
import { QuickStartGeneratingScreen } from '../onboarding/screens/QuickStartGeneratingScreen';
import { OnboardingProvider } from '../onboarding/state';
import { ApiError, apiAuthorizedFetch, buildApiUrl, markOnboardingProgress } from '../lib/api';
import { setJourneyGenerationPending } from '../lib/journeyGeneration';
import { emitOnboardingEvent } from '../lib/telemetry';
import { resolveOnboardingLanguage } from '../onboarding/i18n';
import { POSTLOGIN_LANGUAGE_STORAGE_KEY } from '../i18n/postLoginLanguage';
import { buildDemoUrl } from '../lib/demoEntry';
import { usePageMeta } from '../lib/seo';
import {
  hasModerationSelection,
  writeModerationOnboardingIntentFlag,
} from '../lib/moderationOnboarding';
import {
  ONBOARDING_CANONICAL_URL,
  ONBOARDING_OG_IMAGE_HEIGHT,
  ONBOARDING_OG_IMAGE_TYPE,
  ONBOARDING_OG_IMAGE_URL,
  ONBOARDING_OG_IMAGE_WIDTH,
} from '../lib/onboardingSeo';
import { buildOnboardingOverlayScope } from '../lib/onboardingOverlayScope';
import { clearLocalOnboardingSnapshot, writeLocalOnboardingSnapshotFromPayload } from './labs/mobile-premium/localOnboardingBridge';

async function parseErrorMessage(response: Response) {
  const payload = await response.json().catch(() => ({}));
  if (payload && typeof payload === 'object') {
    const message = (payload as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return response.statusText || 'Error desconocido';
}

type OnboardingIntroPageProps = {
  defaultDashboardPath?: string;
  quickStartDashboardPath?: string;
  quickStartPreviewDashboardPath?: string;
};

export default function OnboardingIntroPage({
  defaultDashboardPath = '/dashboard-v3',
  quickStartDashboardPath = '/labs/mobile-premium/dashboard',
  quickStartPreviewDashboardPath = '/labs/mobile-premium/dashboard?onboardingPreview=1',
}: OnboardingIntroPageProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const variant = location.pathname === '/onboarding2' || location.pathname === '/intro-journey2' ? 'onboarding2' : 'default';
  const language = typeof window !== 'undefined' ? resolveOnboardingLanguage(window.location.search) : 'es';
  const devQuickStartPreview =
    import.meta.env.DEV
    && typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).has('devQuickStart');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [generationMode, setGenerationMode] = useState<string | null>(null);
  const [generationPath, setGenerationPath] = useState<'traditional' | 'quick_start' | null>(null);

  useEffect(() => {
    if (devQuickStartPreview) {
      clearLocalOnboardingSnapshot();
    }
  }, [devQuickStartPreview]);

  usePageMeta({
    title: language === 'en' ? 'Innerbloom Onboarding · Build your base' : 'Innerbloom Onboarding · Crea tu base',
    description:
      language === 'en'
        ? 'Start your Innerbloom onboarding and build your personalized AI foundation in under 3 minutes.'
        : 'Inicia tu onboarding de Innerbloom y crea tu base personalizada con IA en menos de 3 minutos.',
    image: ONBOARDING_OG_IMAGE_URL,
    imageAlt:
      language === 'en'
        ? 'Official preview of the Innerbloom onboarding'
        : 'Preview oficial del onboarding de Innerbloom',
    ogImageSecureUrl: ONBOARDING_OG_IMAGE_URL,
    ogImageType: ONBOARDING_OG_IMAGE_TYPE,
    ogImageWidth: ONBOARDING_OG_IMAGE_WIDTH,
    ogImageHeight: ONBOARDING_OG_IMAGE_HEIGHT,
    twitterImage: ONBOARDING_OG_IMAGE_URL,
    twitterImageAlt:
      language === 'en'
        ? 'Official preview of the Innerbloom onboarding'
        : 'Preview oficial del onboarding de Innerbloom',
    url: ONBOARDING_CANONICAL_URL,
    robots: 'noindex,nofollow',
  });

  const handleFinish = useCallback(
    async (payload: JourneyPayload) => {
      if (isSubmitting) {
        return;
      }

      if (devQuickStartPreview) {
        writeLocalOnboardingSnapshotFromPayload(payload);
        setSubmitError(null);
        setGenerationMode(payload.mode || 'CHILL');
        setGenerationPath(payload.meta.onboarding_path ?? 'quick_start');
        return;
      }

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const introResponse = await apiAuthorizedFetch('/onboarding/intro', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!introResponse.ok) {
          const message = await parseErrorMessage(introResponse);
          throw new ApiError(introResponse.status, { message }, buildApiUrl('/onboarding/intro'));
        }

        const introJson = (await introResponse.json().catch(() => ({}))) as {
          ok?: boolean;
          session_id?: string;
          taskgen_correlation_id?: string | null;
        };

        emitOnboardingEvent('onboarding_completed', {
          mode: payload.mode,
          plan: 'FREE',
          xpTotal: payload.xp.total,
        });

        const hasModerationIntent = hasModerationSelection(payload.data.foundations.body);
        const overlayScope = buildOnboardingOverlayScope({
          user_id: payload.meta.user_id,
          onboarding_session_id: introJson.session_id ?? null,
        });
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(POSTLOGIN_LANGUAGE_STORAGE_KEY, language);
          window.localStorage.setItem('innerbloom.postlogin.language.source', 'locale');
          writeModerationOnboardingIntentFlag(hasModerationIntent, overlayScope);
        }

        if (hasModerationIntent) {
          void markOnboardingProgress('moderation_selected', { trigger: 'onboarding_intro_client' });
        }

        setJourneyGenerationPending({
          clerkUserId: payload.meta.user_id,
          gameMode: payload.mode,
        });
        setGenerationMode(payload.mode);
        setGenerationPath(payload.meta.onboarding_path ?? 'traditional');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Error inesperado';
        const prefix = language === 'en' ? 'We could not save your onboarding.' : 'No pudimos guardar tu onboarding.';
        const suffix = language === 'en' ? 'Try again in a few seconds.' : 'Reintentá en unos segundos.';
        setSubmitError(`${prefix} ${message}. ${suffix}`);
      } finally {
        setIsSubmitting(false);
      }
    },
    [devQuickStartPreview, isSubmitting, language],
  );

  if (generationMode) {
    if (generationPath === 'quick_start') {
      return (
        <QuickStartGeneratingScreen
          language={language}
          gameMode={generationMode}
          isSubmitting={isSubmitting}
          submitCompleted={!isSubmitting}
          submitError={submitError}
          onOpenGuidedDemo={() => {
            navigate(devQuickStartPreview
              ? quickStartPreviewDashboardPath
              : quickStartDashboardPath);
          }}
        />
      );
    }

    return (
      <JourneyGeneratingScreen
        gameMode={generationMode}
        language={language}
        onGoToDashboard={() => navigate(defaultDashboardPath)}
        onOpenGuidedDemo={() => navigate(buildDemoUrl({ language, source: 'onboarding', mode: 'onboarding' }))}
      />
    );
  }

  return (
    <OnboardingProvider>
      <IntroJourney
        language={language}
        onFinish={handleFinish}
        isSubmitting={isSubmitting}
        submitError={submitError}
        skipAuthGateForDev={devQuickStartPreview}
        variant={variant}
      />
    </OnboardingProvider>
  );
}
